import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getAllPromotions } from "../../redux/slices/promotions";

export default function PromoFilter(props = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { applyPromoFilter, setIsLoading = () => { }, platform } = props;

  // State for filter form data
  const [formData, setFormData] = useState({
    status: "all",
    promotionIds: [],
  });

  const [promotionsList, setPromotionsList] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const SELECT_ALL_OPTION = { _id: "__all__", name: "Select All" };

  const applyFiltersHandler = (nextFormData = formData) => {
    const { status, promotionIds } = nextFormData || {};

    // Prepare queryPayload
    const queryPayload = {};
    if (status !== "all") {
      queryPayload.status = status === "true" ? true : status === "false" ? false : status;
    }
    if (promotionIds && promotionIds.length > 0) {
      // Map objects to IDs if they are objects
      const ids = promotionIds.map((p) => (typeof p === "object" ? p._id : p));
      queryPayload.promotionId = ids;
    }

    const filterValue = { ...queryPayload };

    // Preserve existing query params
    const existingParams = new URLSearchParams(location.search);

    // Merge updated filters
    Object.entries(filterValue).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        existingParams.set(key, JSON.stringify(value));
      } else if (value !== "" && value !== undefined && value !== null) {
        if (typeof value === "boolean") {
          existingParams.set(key, value.toString());
        } else if (typeof value === "string" && value.trim() !== "") {
          existingParams.set(key, value);
        }
      }
    });

    // Remove params if they are "all" or empty

    if (status === "all") {
      existingParams.delete("status");
    }
    if (!promotionIds || promotionIds.length === 0) {
      existingParams.delete("promotionId");
    }

    navigate(`${location.pathname}?${existingParams.toString()}`);
    applyPromoFilter({ queryPayload });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const status = searchParams.get("status") || "all";

    let promotionIds = [];
    if (searchParams.get("promotionId")) {
      try {
        promotionIds = JSON.parse(searchParams.get("promotionId")) || [];
      } catch {
        promotionIds = [];
      }
    }

    const nextFormData = {
      status,
      promotionIds,
    };
    setFormData(nextFormData);

    // Keep grid data in sync with URL on back/forward navigation (and on initial load)
    const queryPayload = {};
    if (status !== "all") {
      queryPayload.status = status === "true" ? true : status === "false" ? false : status;
    }
    if (promotionIds && promotionIds.length > 0) {
      const ids = promotionIds.map((p) => (typeof p === "object" ? p._id : p));
      queryPayload.promotionId = ids;
    }

    applyPromoFilter({ queryPayload });
    setIsLoading(false);
  }, [location.search, setIsLoading]);

  //  useEffect(() => {
  //   if (platform) {
  //     fetchPromotions({ searchVal: "", skipVal: 0, append: false });
  //   }
  // }, [platform]);

  // Reset promotions list when platform changes
  useEffect(() => {
    if (!platform) return;
    setPromotionsList([]);
    fetchPromotions();
  }, [platform]);

  useEffect(() => {
    // If we have IDs but they are not objects (missing names), fetch all to resolve names
    // This restores previous logic but ONLY runs if we have unresolved IDs
    const unresolvedIds = formData.promotionIds?.filter((id) => typeof id !== "object");

    if (unresolvedIds && unresolvedIds.length > 0) {
      setIsLoading(true);
      dispatch(
        getAllPromotions({
          skip: 0,
          limit: 100, // Increased to better resolve names across pages
          platform: platform,
        })
      )
        .unwrap()
        .then((res) => {
          const { data = [] } = res;
          // Map IDs to objects
          const updatedPromotions = formData.promotionIds.map((id) => {
            if (typeof id === "object") return id;
            const found = data.find((p) => p._id === id);
            return found ? { _id: found._id, name: found.name } : id; // Keep ID if not found
          });

          // Avoid infinite loop: only update if something changed to an object
          const resolvedCount = updatedPromotions.filter((p) => typeof p === "object").length;
          const originalResolvedCount = formData.promotionIds.filter(
            (p) => typeof p === "object"
          ).length;

          if (resolvedCount > originalResolvedCount) {
            setFormData((prev) => ({ ...prev, promotionIds: updatedPromotions }));
          }
        })
        .catch(() => { })
        .finally(() => setIsLoading(false));
    }
  }, [formData.promotionIds, dispatch]);

  const clearAllHandler = () => {
    setFormData({
      // platform: [],
      status: "all",
      promotionIds: [],
    });

    const params = new URLSearchParams(location.search);

    // Remove specific params
    params.delete("status");
    params.delete("promotionId");

    // Update the URL without reloading the page
    navigate(`${location.pathname}?${params.toString()}`);

    // Apply empty filter
    applyPromoFilter({ queryPayload: {} });
  };

  const fetchPromotions = () => {
    setPromoLoading(true);
    return dispatch(
      getAllPromotions({
        skip: 0,
        limit: 1000,
        platform: platform,
      })
    )
      .unwrap()
      .then((res) => {
        const { data, total } = res;
        const promoData = data?.map((item) => ({
          _id: item._id,
          name: item.name,
        }));
        setPromotionsList(promoData || []);
      })
      .catch(() => { })
      .finally(() => setPromoLoading(false));
  };

  const selectedPromotionOptions = (formData?.promotionIds || [])
    .map((item) => {
      if (typeof item === "object") return item;
      return promotionsList.find((promo) => promo._id === item) || null;
    })
    .filter(Boolean);
  const selectedCount = selectedPromotionOptions.length;

  // Initial load is now primarily handled by the platform reset useEffect
  // but we keep this to ensure it runs if platform is stable

  return (
    <Box className="cus-filter-sec" mb={2}>
      <Grid className="filter-box" container alignItems="center" spacing={1.5}>
        <Grid item xs={12} sm={4} md={3} xxl={3}>
          <Box className="filter-pro-status">
            <FormControl fullWidth className="select-arrow-size promition-name-field" size="small">
              <Autocomplete
                multiple
                disableCloseOnSelect
                disableClearable
                size="small"
                className="select-arrow-size"
                popupIcon={<ArrowDropDownIcon />}
                options={[SELECT_ALL_OPTION, ...promotionsList]}
                loading={promoLoading}
                value={selectedPromotionOptions}
                onChange={(_, newValue, reason, details) => {
                  if (reason === "clear") {
                    const nextFormData = { ...formData, promotionIds: [] };
                    setFormData(nextFormData);
                    applyFiltersHandler(nextFormData);
                    return;
                  }

                  const clickedOption = details?.option;
                  if (clickedOption?._id === SELECT_ALL_OPTION._id) {
                    const allSelected = promotionsList.length > 0 &&
                      promotionsList.every((promo) =>
                        selectedPromotionOptions.some((selected) => selected._id === promo._id)
                      );
                    const nextFormData = {
                      ...formData,
                      promotionIds: allSelected ? [] : promotionsList,
                    };
                    setFormData(nextFormData);
                    applyFiltersHandler(nextFormData);
                    return;
                  }

                  const nextFormData = {
                    ...formData,
                    promotionIds: newValue.filter((option) => option._id !== SELECT_ALL_OPTION._id),
                  };
                  setFormData(nextFormData);
                  applyFiltersHandler(nextFormData);
                }}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                filterOptions={(options, state) => {
                  const q = String(state.inputValue || "").toLowerCase().trim();
                  const filtered = options.filter((opt) => {
                    if (opt._id === SELECT_ALL_OPTION._id) return false;
                    return !q || String(opt.name || "").toLowerCase().includes(q);
                  });
                  return [SELECT_ALL_OPTION, ...filtered];
                }}
                renderOption={(props, option) => {
                  const isSelectAll = option._id === SELECT_ALL_OPTION._id;
                  const allSelected = promotionsList.length > 0 &&
                    promotionsList.every((promo) =>
                      selectedPromotionOptions.some((selected) => selected._id === promo._id)
                    );
                  const checked = isSelectAll
                    ? allSelected
                    : selectedPromotionOptions.some((selected) => selected._id === option._id);

                  return (
                    <li {...props} key={option._id}>
                      <Checkbox
                        checked={checked}
                        size="small"
                        sx={{ mr: 1, p: 0.5, color: "#A0A7B4", "&.Mui-checked": { color: "#2D6CEA" } }}
                      />
                      <span style={{ fontSize: 14, color: "#2E3445" }}>{option.name}</span>
                    </li>
                  );
                }}
                renderTags={() => null}
                ListboxProps={{
                  sx: {
                    py: 0.75,
                    borderRadius: 2,
                    maxHeight: 300,
                    "& .MuiAutocomplete-option": {
                      minHeight: 38,
                      px: 1.5,
                      borderRadius: 1,
                      mx: 0.75,
                      my: 0.25,
                      "&[aria-selected='true']": {
                        backgroundColor: "#EEF3FF",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#F4F6FA",
                      },
                    },
                  },
                }}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    gap: 0.5,
                  },
                  "&.select-arrow-size svg": {
                    width: "1.1em !important",
                    height: "1.1em !important",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Promotion Name"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#F3F4F6",
                        borderRadius: 2,
                        minHeight: 40,
                        paddingRight: "36px !important",
                        alignItems: "center",
                      },
                      "& .MuiAutocomplete-inputRoot": {
                        backgroundColor: "#F3F4F6 !important",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                      "& .MuiInputBase-input": {
                        backgroundColor: "transparent !important",
                      },
                      "& .MuiAutocomplete-input": {
                        minWidth: 90,
                        fontSize: 14,
                        backgroundColor: "transparent !important",
                      },
                      "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
                        WebkitBoxShadow: "0 0 0 1000px #F3F4F6 inset !important",
                        WebkitTextFillColor: "inherit",
                      },
                      "& .MuiAutocomplete-popupIndicator": {
                        p: 0.25,
                        mr: 0.25,
                        color: "rgba(0, 0, 0, 0.54)",
                        backgroundColor: "transparent !important",
                      },
                      "& .MuiAutocomplete-popupIndicator .MuiSvgIcon-root": {
                        fontSize: "1rem !important",
                      },
                      "& .MuiAutocomplete-endAdornment": {
                        right: 6,
                        backgroundColor: "transparent !important",
                      },
                      "& .MuiAutocomplete-clearIndicator": {
                        display: "none",
                      },
                      "& .MuiAutocomplete-popupIndicator svg": {
                        backgroundColor: "transparent !important",
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {promoLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={4} md={3} xxl={2}>
          <Box className="filter-pro-status">
            <FormControl fullWidth className="select-arrow-size">
              <InputLabel>Status</InputLabel>
              <Select
                size="small"
                value={formData?.status || "all"}
                onChange={(e) => {
                  const nextFormData = { ...formData, status: e.target.value };
                  setFormData(nextFormData);
                  applyFiltersHandler(nextFormData);
                }}
                fullWidth
                className="form-select"
                name="status"
                label="Status"
                sx={{
                  "& .MuiSelect-icon": {
                    fontSize: "1rem !important",
                    width: "1.1em !important",
                    height: "1.1em !important",
                    color: "rgba(0, 0, 0, 0.54)",
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={4} md={4} xxl={1.5}>
          <Box className="filter-btns">
            <Stack direction="row" alignItems="center" gap={1} className="w-10">
              <Button
                className="filter-clear-all"
                onClick={clearAllHandler}
                size="small"
                variant="text"
                color="secondary"
                style={{ width: "100%" }}
              >
                Clear All
              </Button>
            </Stack>
          </Box>
        </Grid>

        {selectedPromotionOptions.length > 0 && (
          <Grid item xs={12}>
            <Box
              sx={{
                mt: 0.5,
                px: 1.25,
                py: 0.75,
                borderRadius: 2,
                bgcolor: "#F8FAFC",
                border: "1px solid #E8EDF5",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                overflowX: "auto",
                "&::-webkit-scrollbar": { height: 5 },
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#6B7280", fontWeight: 700, flexShrink: 0 }}
              >
                Selected Promotions:
              </Typography>

              {selectedPromotionOptions.map((option) => (
                <Chip
                  key={option._id}
                  size="small"
                  label={option.name}
                  onDelete={() => {
                    const nextSelected = selectedPromotionOptions.filter(
                      (selected) => selected._id !== option._id
                    );
                    const nextFormData = { ...formData, promotionIds: nextSelected };
                    setFormData(nextFormData);
                    applyFiltersHandler(nextFormData);
                  }}
                  sx={{
                    maxWidth: 220,
                    height: 24,
                    bgcolor: "#E9EDF5",
                    flexShrink: 0,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              ))}

              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const nextFormData = { ...formData, promotionIds: [] };
                  setFormData(nextFormData);
                  applyFiltersHandler(nextFormData);
                }}
                sx={{ ml: "auto", minWidth: "auto", px: 0.5, flexShrink: 0 }}
              >
                Clear all
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
